import React from 'react';

const RELOAD_ATTEMPTED_KEY = 'fininteract-chunk-reload-attempted';

// How long a just-set flag "counts" as the immediate aftermath of that same reload. Long
// enough that the post-reload re-render of the same failing route is always still inside this
// window (so it correctly stops instead of looping); short enough that a genuinely later,
// unrelated failure in the same long-lived tab still gets its own fresh auto-retry.
const RELOAD_COOLDOWN_MS = 30_000;

// Every route is React.lazy()-loaded, fetching a separate, content-hashed chunk file the
// first time it's needed. A tab left open across a deploy still has the OLD build in memory --
// when it then lazy-loads a chunk for a route it hasn't visited yet, it requests the OLD
// hash, which the new deploy's `rsync --delete` already removed, 404ing with a ChunkLoadError.
// Suspense only handles the *loading* state, not this *failure* state, so without a boundary
// here that error was propagating uncaught and white-screening the whole app.
function isChunkLoadError(error: Error): boolean {
  return (
    error.name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(error.message) ||
    /Failed to fetch dynamically imported module/i.test(error.message)
  );
}

// A plain boolean flag doesn't work here: window.location.reload() re-executes the whole app
// from scratch, so "was a reload just attempted" can't live in a mount effect (that mount
// itself happens on every reload, including the one this flag is meant to survive) -- it has
// to be judged by how recently the flag was set, checked only inside the catch itself.
function recentlyAttemptedReload(): boolean {
  const last = sessionStorage.getItem(RELOAD_ATTEMPTED_KEY);
  return last !== null && Date.now() - Number(last) < RELOAD_COOLDOWN_MS;
}

interface State {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    if (isChunkLoadError(error) && !recentlyAttemptedReload()) {
      sessionStorage.setItem(RELOAD_ATTEMPTED_KEY, String(Date.now()));
      window.location.reload();
    }
    // Otherwise: either not a chunk error, or we already tried reloading for one within the
    // cooldown window and it didn't help -- fall through to the manual fallback below rather
    // than looping.
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-paper dark:bg-ink-950 flex items-center justify-center px-4">
          <div className="text-center max-w-sm">
            <p className="font-serif text-2xl text-ink dark:text-paper mb-2">Something went wrong</p>
            <p className="text-sm text-ink-60 dark:text-ink-20 mb-6">
              This page may have updated since you opened it. Reloading usually fixes it.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="btn-primary inline-flex items-center justify-center"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
